<template>
  <div class="avatar-display" :class="[sizeClass, shapeClass, className]" :style="style">
    <!-- Background Layer -->
    <div v-if="background" class="avatar-layer avatar-background">
      <img :src="background.imagePath" :alt="background.name" loading="lazy" decoding="async"
        class="avatar-image object-cover" />
    </div>

    <!-- Animal/Pose Layer -->
    <div class="avatar-layer avatar-main">
      <img :src="displayImage" :alt="displayName" loading="lazy" decoding="async" class="avatar-image object-contain" />
    </div>

    <!-- Accessories Layers (sorted by zIndex) -->
    <div v-for="accessory in sortedAccessories" :key="accessory.id" class="avatar-layer avatar-accessory"
      :style="{ zIndex: accessory.zIndex || 50 }">
      <img :src="accessory.imagePath" :alt="accessory.name" loading="lazy" decoding="async"
        class="avatar-image object-contain" />
    </div>

    <!-- Level Badge (optional) -->
    <div v-if="showLevel && level !== undefined" class="avatar-level-badge">
      <span class="avatar-level-text">Niv. {{ level }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type CSSProperties } from 'vue'
import type { AnimalAvatar, AvatarItem } from '@/models/Avatar'

interface Props {
  animal?: AnimalAvatar | null
  pose?: AvatarItem | null
  accessories?: AvatarItem[]
  background?: AvatarItem | null
  level?: number
  showLevel?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  shape?: 'rounded' | 'square'
  className?: string
  style?: CSSProperties
}

const props = withDefaults(defineProps<Props>(), {
  animal: null,
  pose: null,
  accessories: () => [],
  background: null,
  level: undefined,
  showLevel: false,
  size: 'md',
  shape: 'rounded',
  className: '',
  style: () => ({}),
})

// Compute display image (pose takes precedence over base animal image)
const displayImage = computed(() => {
  if (props.pose) {
    return props.pose.imagePath
  }
  if (props.animal) {
    return props.animal.baseImage
  }
  // Fallback to dog happy pose (first animal available at level 0)
  return '/illustrations/animals/dog/dog-happy-optimized.webp'
})

const displayName = computed(() => {
  if (props.pose) {
    return props.pose.name
  }
  if (props.animal) {
    return props.animal.name
  }
  return 'Avatar'
})

// Sort accessories by zIndex for proper layering
const sortedAccessories = computed(() => {
  return [...props.accessories].sort((a, b) => {
    const zIndexA = a.zIndex || 50
    const zIndexB = b.zIndex || 50
    return zIndexA - zIndexB
  })
})

const sizeClass = computed(() => `avatar-size-${props.size}`)
const shapeClass = computed(() => `rounded-${props.shape}`)
</script>

<style scoped>
.avatar-display {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-image {
  width: 100%;
  height: 100%;
}

.avatar-background {
  z-index: 0;
}

.avatar-main {
  z-index: 10;
}

.avatar-accessory {
  /* zIndex set dynamically via style binding */
}

.avatar-level-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 1000;
}

.avatar-level-text {
  white-space: nowrap;
}

/* Size variants */
.avatar-size-sm {
  max-width: 80px;
}

.avatar-size-md {
  max-width: 150px;
}

.avatar-size-lg {
  max-width: 250px;
}

.avatar-size-xl {
  max-width: 400px;
}

/* Fill container completely (used for wide background previews) */
.avatar-size-full {
  max-width: none;
  width: 100%;
  height: 100%;
  aspect-ratio: auto;
}

/* Responsive sizing */
@media (max-width: 640px) {
  .avatar-size-lg {
    max-width: 200px;
  }

  .avatar-size-xl {
    max-width: 300px;
  }
}

.rounded-square {
  border-radius: 0px;
}

.rounded-rounded {
  border-radius: 100%;
}
</style>
